import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { CreditCounter } from './CreditComponents';
import logo from '../assets/logo.png';

const FeatureGate = ({ 
  feature = 'Job Tracker',
  headline = 'Unlock the Job Tracker',
  description = 'Track up to 20 active job applications, set reminders, and never lose track of where you applied.',
  benefits = [
    'Track status privately (Data syncs to your Google Drive)',
    'Track status: Applied, Interview, Offer, Rejected',
    'Connect to Google Drive for data backup',
    'Set reminders for follow-ups'
  ]
}) => {
  const [searchParams] = useSearchParams();
  const [promoCode, setPromoCode] = useState(null);
  const [showPromoAlert, setShowPromoAlert] = useState(false);
  
  // Check for promo code in URL (e.g., ?promo=RESET5)
  useEffect(() => {
    const promo = searchParams.get('promo');
    if (promo) {
      setPromoCode(promo.toUpperCase());
      setShowPromoAlert(true);
      // Store in localStorage for the auth callback to use
      localStorage.setItem('promo_code', promo.toUpperCase());
    }
  }, [searchParams]);
  
  const handleGoogleSignIn = async () => {
    // Redirect to Google OAuth with promo code (if present)
    await authAPI.initiateGoogleLogin('/auth/callback', promoCode);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="AlignifyNOW" className="h-[80px] w-auto" />
              <span className="text-xl font-bold tracking-tight text-slate-900 hidden sm:inline">
                Alignify<span className="text-blue-600">NOW</span>
              </span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Match Score
              </Link>
              <Link to="/tools/future-proof" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:flex items-center gap-1">
                Future-Proof
                <span className="px-1.5 py-0.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-[10px] font-bold rounded-full">NEW</span>
              </Link>
              <Link to="/tools/cover-letter" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Cover Letter
              </Link>
              <Link to="/tools/mock-interview" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Mock Interview
              </Link>
              <Link to="/blog" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Blog
              </Link>
              <Link to="/checklist" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Free Checklist
              </Link>
              <Link to="/pricing" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Pricing
              </Link>
              {/* Credit Counter - Shows for authenticated users */}
              <CreditCounter className="hidden sm:flex" />
            </div>
          </div>
        </div>
      </nav>

      {/* Promo Code Banner */}
      {showPromoAlert && promoCode === 'RESET5' && (
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-2 text-center">
            <span className="text-lg">🎁</span>
            <p className="text-sm sm:text-base font-medium">
              <strong>RESET5</strong> promo code detected! Sign up now to get <strong>5 bonus credits</strong> (8 total).
            </p>
          </div>
        </div>
      )}

      {/* Feature Gate Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 lg:py-16">
        {/* Compact Header with Sign In */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {headline}
              </h1>
              <p className="text-gray-600">
                Sign in to save and track your job applications from anywhere.
              </p>
            </div>
            
            {/* Google Sign In Button - Prominent */}
            <div className="flex-shrink-0">
              <button
                onClick={handleGoogleSignIn}
                className="inline-flex items-center px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-md w-full md:w-auto justify-center"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </button>
              <p className="mt-2 text-xs text-gray-500 text-center">
                Quick & secure — no passwords
              </p>
            </div>
          </div>
        </div>

        {/* Benefits Section - Below the fold */}
        <div className="bg-gray-50 rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </span>
            What you get with {feature}
          </h2>
          
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-start">
                <svg className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-700 text-sm">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Privacy Note */}
        <div className="text-center mb-8">
          <p className="text-sm text-gray-500">
            🔒 We only access your name and email from Google.{' '}
            <Link to="/privacy" className="text-purple-600 hover:underline">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>

      {/* Alternative: Try Free Tools */}
      <div className="bg-gray-50 py-12 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Not ready to sign in?
          </h3>
          <p className="text-gray-600 mb-6">
            Try our free tools — no account required!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-6 py-3 bg-white text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors border border-blue-200"
            >
              Check Match Score
            </Link>
            <Link
              to="/tools/cover-letter"
              className="px-6 py-3 bg-white text-purple-600 font-medium rounded-lg hover:bg-purple-50 transition-colors border border-purple-200"
            >
              Generate Cover Letter Intro
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <span className="text-xl font-bold text-white opacity-80">
                Alignify<span className="text-blue-400">NOW</span>
              </span>
            </div>
            <div className="text-gray-400 text-xs mb-4 md:mb-0">
              © 2026 AlignifyNOW. All rights reserved. Built by Matambanadzo Studio.
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/blog" className="text-gray-400 hover:text-white text-sm transition-colors">
                Blog
              </Link>
              <Link to="/affiliates" className="text-gray-400 hover:text-white text-sm transition-colors">
                Affiliates
              </Link>
              <Link to="/checklist" className="text-gray-400 hover:text-white text-sm transition-colors">
                Free Checklist
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeatureGate;
